import { computed, signal } from '@lit-labs/signals';
import { connectFirestoreToEmulator, enableFirestoreOfflinePersistence, getFirestoreDb, } from '@df/firebase';
import { FirestoreCollectionStore } from './firestore-base.store.js';
import { Timestamp, collection, orderBy, where, } from 'firebase/firestore';
const FIRESTORE_HOST = '127.0.0.1';
const FIRESTORE_PORT = 8280;
const COLLECTION_PATH = 'todos';
const defaultFilters = {
    showCompleted: true,
    priority: 'all',
    tag: 'all',
    search: '',
};
const filterSignal = signal({ ...defaultFilters });
const fallbackStateSignal = signal({
    status: 'idle',
    documents: [],
    error: null,
    isListening: false,
    lastUpdated: null,
    currentPage: 1,
    pageSize: 5,
    hasNextPage: false,
    hasPreviousPage: false,
    queryDescription: 'All todos',
});
let collectionRef = null;
const storeSignal = signal(null);
export const todoCollectionState = computed(() => {
    const store = storeSignal.get();
    return store ? store.state.get() : fallbackStateSignal.get();
});
export const todoFilterState = computed(() => filterSignal.get());
export async function initializeTodosStore(app, useEmulator) {
    if (storeSignal.get()) {
        return;
    }
    const db = getFirestoreDb(app);
    if (useEmulator) {
        connectFirestoreToEmulator(db, {
            host: FIRESTORE_HOST,
            port: FIRESTORE_PORT,
        });
    }
    await enableFirestoreOfflinePersistence(db);
    collectionRef = collection(db, COLLECTION_PATH);
    const newStore = new FirestoreCollectionStore(collectionRef, {
        defaultConstraints: [orderBy('createdAt', 'desc')],
        defaultQueryDescription: 'All todos (newest first)',
        pageSize: 5,
        mapDocument: normalizeFromFirestore,
    });
    storeSignal.set(newStore);
    await applyTodoFilters();
}
export async function addTodo(draft) {
    const activeStore = ensureStore();
    const now = new Date();
    const payload = {
        title: draft.title,
        description: draft.description,
        completed: false,
        priority: draft.priority,
        tags: draft.tags,
        createdAt: now,
        updatedAt: now,
        dueDate: draft.dueDate,
        titleLower: draft.title.toLowerCase(),
    };
    const id = await activeStore.create(payload);
    await activeStore.refresh();
    return id;
}
export async function updateTodo(id, updates) {
    const activeStore = ensureStore();
    const payload = {};
    if (typeof updates.title === 'string') {
        payload.title = updates.title;
        payload.titleLower = updates.title.toLowerCase();
    }
    if (typeof updates.description === 'string') {
        payload.description = updates.description;
    }
    if (typeof updates.priority === 'string') {
        payload.priority = updates.priority;
    }
    if (Array.isArray(updates.tags)) {
        payload.tags = updates.tags;
    }
    if (updates.dueDate !== undefined) {
        payload.dueDate = updates.dueDate;
    }
    if (typeof updates.completed === 'boolean') {
        payload.completed = updates.completed;
    }
    payload.updatedAt = new Date();
    await activeStore.update(id, payload);
    await activeStore.refresh();
}
export async function toggleTodoCompletion(id, completed) {
    await updateTodo(id, { completed });
}
export async function deleteTodo(id) {
    const activeStore = ensureStore();
    await activeStore.delete(id);
    await activeStore.refresh();
}
export async function loadNextTodoPage() {
    const activeStore = ensureStore();
    await activeStore.loadNextPage();
}
export async function loadPreviousTodoPage() {
    const activeStore = ensureStore();
    await activeStore.loadPreviousPage();
}
export async function setTodoPageSize(size) {
    const activeStore = ensureStore();
    await activeStore.setPageSize(size);
}
export function startTodoRealtime() {
    ensureStore().startRealtime();
}
export function stopTodoRealtime() {
    ensureStore().stopRealtime();
}
export async function setTodoFilters(next) {
    filterSignal.set({ ...filterSignal.get(), ...next });
    await applyTodoFilters();
}
export async function resetTodoFilters() {
    filterSignal.set({ ...defaultFilters });
    await applyTodoFilters();
}
async function applyTodoFilters() {
    const activeStore = ensureStore();
    const filters = filterSignal.get();
    const constraints = [];
    const descriptions = [];
    if (!filters.showCompleted) {
        constraints.push(where('completed', '==', false));
        descriptions.push('Incomplete only');
    }
    if (filters.priority !== 'all') {
        constraints.push(where('priority', '==', filters.priority));
        descriptions.push(`Priority: ${filters.priority}`);
    }
    if (filters.tag !== 'all') {
        constraints.push(where('tags', 'array-contains', filters.tag));
        descriptions.push(`Tag: ${filters.tag}`);
    }
    const description = descriptions.length ? descriptions.join(' • ') : 'All todos (newest first)';
    await activeStore.setQuery(constraints, description);
}
function ensureStore() {
    const store = storeSignal.get();
    if (!store || !collectionRef) {
        throw new Error('Todo store has not been initialized. Call initializeTodosStore() first.');
    }
    return store;
}
function normalizeFromFirestore(doc) {
    const createdAt = doc.createdAt instanceof Timestamp ? doc.createdAt.toDate() : doc.createdAt ?? null;
    const updatedAt = doc.updatedAt instanceof Timestamp ? doc.updatedAt.toDate() : doc.updatedAt ?? null;
    const dueDate = doc.dueDate instanceof Timestamp ? doc.dueDate.toDate() : doc.dueDate ?? null;
    return {
        ...doc,
        createdAt,
        updatedAt,
        dueDate,
        titleLower: doc.titleLower ?? doc.title.toLowerCase(),
    };
}
export function __setTodoDemoState(state) {
    if (storeSignal.get()) {
        throw new Error('Cannot set demo state after the todo store has been initialized.');
    }
    fallbackStateSignal.set(state);
}
export function __setTodoDemoFilters(filters) {
    if (storeSignal.get()) {
        throw new Error('Cannot set demo filters after the todo store has been initialized.');
    }
    filterSignal.set(filters);
}
if (typeof globalThis === 'object') {
    const target = globalThis;
    target.__dfSetTodoDemoState = __setTodoDemoState;
    target.__dfSetTodoDemoFilters = __setTodoDemoFilters;
}
//# sourceMappingURL=todos.store.js.map